import React, { useState, useContext, useEffect } from 'react';
import { KFLButton, KFLDialog } from '@kamstrup/kamstrup-frontend-library';
import { connect } from 'react-redux';
import Translate from '../../shared/localization/localize';
import { appRoles } from '../../../core/security/const';
import * as Devices from '../../../modules/devices';
import * as Download from '../../../modules/dialogs/download';
import * as m from '../../../modules/dialogs/download/messages';
import DownloadFileType from './downloadFileType';
import DownloadTotals from './downloadTotals';
import DownloadMode from './downloadMode';
import DialogHeader from '../../shared/dialog/dialogHeader';
import DialogSection from '../../shared/dialog/dialogSection';
import DialogFooter from '../../shared/dialog/dialogFooter';
import { ReduxState } from '../../../interfaces';
import { Groups } from '../../../models';
import { MessageType } from '../../../core/localization';
import Spinner from '../../shared/spinner';
import { LocaleCtx } from '../../../contexts/locale.context';
import { setItems } from '../../../modules/dialogs/download';
import { openDialog } from '../../../modules/dialogs/downloadLogs';
import useUserEKS from '../../../hooks/useUserEKS';
import useDialog from '../../shared/dialog/useDialog';
import './downloadDialog.scss';
import { hasRole } from '../../../libs/security/utils';

interface StateProps extends Download.State {
  selectedDevices: string[];
  selectedOrders: string[];
  selectedProducts: string[];
  selectedCustomers: string[];
}

type Props = StateProps;

const DownloadDialog: React.FC<Props> = (props) => {
  const { setDialog } = useDialog();
  const { translate } = useContext(LocaleCtx);
  const { roles: userRoles } = useUserEKS();

  const [filetype, setFiletype] = useState(Download.FileTypes.CSV);
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState('' as MessageType);

  useEffect(() => {
    if (props.groupedBy === Groups.DEVICES) setItems(props.selectedDevices);
  }, [props.groupedBy, props.selectedDevices]);

  useEffect(() => {
    if (props.groupedBy === Groups.ORDERS) setItems(props.selectedOrders);
  }, [props.groupedBy, props.selectedOrders]);

  useEffect(() => {
    if (props.groupedBy === Groups.DEVICETYPES)
      setItems(props.selectedProducts);
  }, [props.groupedBy, props.selectedProducts]);

  useEffect(() => {
    if (props.groupedBy === Groups.CUSTOMERS) setItems(props.selectedCustomers);
  }, [props.groupedBy, props.selectedCustomers]);

  const totals = Download.getTotals(props.groupedBy, props.mode, props.keys);
  const numDevices = Devices.totalDevices(totals);

  const hasManyFiles =
    Devices.numNoMetersDevices(totals) > 0 &&
    Devices.numMetersDevices(totals) > 0;

  const fileOpts = hasRole(userRoles, [appRoles.user, appRoles.superuser])
    ? Download.FileTypesList
    : Download.FileTypesList.filter((o) => o === Download.FileTypes.CSV);

  const content = (
    <>
      <DialogSection>
        <DownloadMode
          key="md-download-mode"
          keys={props.keys}
          groupedBy={props.groupedBy}
          mode={props.mode}
        />
      </DialogSection>
      <DialogSection scrollable>
        <DownloadTotals key="md-download-totals" totals={totals} />
      </DialogSection>
      <DialogSection isMain scrollable>
        <DownloadFileType
          key="md-download-options"
          fileTypeOptions={fileOpts}
          fileType={filetype}
          setFileType={setFiletype}
          pwd={pwd}
          setPwd={setPwd}
          error={error}
          numberOfFiles={hasManyFiles}
          translate={translate}
        />
      </DialogSection>
    </>
  );

  const onClick = () => {
    if (Download.isDownloadable(filetype, pwd)) {
      setError('');
      Download.download(filetype, pwd);
    } else {
      setError(m.msgKEMPwdError);
    }
  };
  const downloadButton = (
    <KFLButton
      id="md-download-bt-download"
      key="md-download-bt-download"
      variant="primary"
      disabled={numDevices === 0}
      onClick={onClick}
    >
      <Translate message={m.msgDownloadBtn} />
    </KFLButton>
  );

  const canViewHistory = true; // hasRole(props.userRoles, [appRoles.superuser, appRoles.user])
  const historyButton = canViewHistory ? (
    <KFLButton
      id="md-download-bt-history"
      key="md-download-bt-history"
      variant="borderless"
      onClick={openDialog}
    >
      <Translate message={m.msgHistoryBtn} />
    </KFLButton>
  ) : (
    <></>
  );

  const footerText = props.isFetching ? (
    <Spinner label={m.msgDownloading} iconSize={20} />
  ) : (
    historyButton
  );

  return (
    <KFLDialog
      header={
        <DialogHeader
          title={<Translate message={m.msgHeader} />}
          fnCloseDialog={() => setDialog?.()}
        />
      }
      sections={[content]}
      footer={
        <DialogFooter
          leftContent={footerText}
          buttons={[downloadButton]}
          fnCloseDialog={() => setDialog?.()}
        />
      }
    />
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  ...state.dialogState.downloadState,
  selectedDevices: state.devicesState.selection.selectedKeys,
  selectedOrders: state.ordersState.selection.selectedKeys,
  selectedProducts: state.deviceTypesState.selection.selectedKeys,
  selectedCustomers: state.customersState.selection.selectedKeys,
});

export default connect(mapStateToProps)(DownloadDialog);
